import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { NoteForApproval } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, FileCheck, Tag, ShieldCheck, TrendingUp } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const NfaEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [nfa, setNfa] = useState<NoteForApproval | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [backgroundContext, setBackgroundContext] = useState('');
  const [commercialJustification, setCommercialJustification] = useState('');
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [savingsEstimated, setSavingsEstimated] = useState<number>(0);
  const [costCenterCode, setCostCenterCode] = useState('');
  const [plantCode, setPlantCode] = useState('');
  const [capexOpex, setCapexOpex] = useState<'CAPEX' | 'OPEX'>('OPEX');

  useEffect(() => {
    const loadNfa = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockNfaLfaService.getNfaById(id);
        if (!data) {
          navigate(ROUTES.NFA.LIST);
          return;
        }
        setNfa(data);
        setTitle(data.title || '');
        setSubject(data.subject || '');
        setBackgroundContext(data.backgroundContext || '');
        setCommercialJustification(data.commercialJustification || '');
        setEstimatedCost(data.estimatedCost || 0);
        setSavingsEstimated(data.savingsEstimated || 0);
        setCostCenterCode(data.costCenterCode || '');
        setPlantCode(data.plantCode || '');
        setCapexOpex(data.capexOpex || 'OPEX');
      } finally {
        setIsLoading(false);
      }
    };

    loadNfa();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !nfa) return;

    try {
      setIsSaving(true);
      await mockNfaLfaService.updateNfa(id, {
        title,
        subject,
        backgroundContext,
        commercialJustification,
        estimatedCost: Number(estimatedCost),
        savingsEstimated: Number(savingsEstimated),
        costCenterCode,
        plantCode,
        capexOpex,
      });
      navigate(ROUTES.NFA.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update Note for Approval.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Note for Approval Editor...
      </div>
    );
  }

  if (!nfa) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">NFA Record Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.NFA.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to NFA Ledger
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.NFA.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {nfa.nfaNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Note for Approval
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Linked Requisition: <span className="font-mono font-bold text-primary">{nfa.requestNumber}</span> • Prepared by {nfa.preparedByName}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.NFA.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-nfa-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-nfa-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Commercial Scope & Purpose */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <FileCheck className="h-4 w-4 text-primary" />
              Commercial Scope & Identification
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div>
              <label className="block font-bold text-foreground mb-1.5">
                NFA Title / Subject Heading *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                placeholder="e.g. Enterprise Server Infrastructure Replacement & Modernization"
              />
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Executive Purpose & Scope Summary
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                placeholder="Brief summary of the business need and commercial engagement..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Operational Requirement Context *
                </label>
                <textarea
                  value={backgroundContext}
                  onChange={(e) => setBackgroundContext(e.target.value)}
                  required
                  rows={4}
                  className="w-full rounded-xl border border-input bg-background p-3 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins leading-relaxed"
                  placeholder="Explain why this procurement is urgently needed by the department..."
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Commercial Justification & Market Rates *
                </label>
                <textarea
                  value={commercialJustification}
                  onChange={(e) => setCommercialJustification(e.target.value)}
                  required
                  rows={4}
                  className="w-full rounded-xl border border-input bg-background p-3 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins leading-relaxed"
                  placeholder="Detail supplier negotiation, competitive RFQ comparisons, and benchmark rates..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Financial Commitments & Cost Allocation */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Financial Commitments & Savings Clearance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Proposed Commitment (₹ INR) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-foreground-muted">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={estimatedCost}
                    onChange={(e) => setEstimatedCost(Number(e.target.value))}
                    required
                    min={0}
                    step={1}
                    className="w-full rounded-xl border border-input bg-background pl-8 pr-3 py-2.5 text-xs font-bold text-foreground font-mono focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                  Verified Negotiated Savings (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-emerald-600">
                    +₹
                  </span>
                  <input
                    type="number"
                    value={savingsEstimated}
                    onChange={(e) => setSavingsEstimated(Number(e.target.value))}
                    min={0}
                    step={1}
                    className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-2.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 font-mono focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Cost Center Code *
                </label>
                <input
                  type="text"
                  value={costCenterCode}
                  onChange={(e) => setCostCenterCode(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-bold font-mono text-foreground focus:ring-2 focus:ring-primary"
                  placeholder="e.g. CC-ENG-01"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Plant Code *
                </label>
                <input
                  type="text"
                  value={plantCode}
                  onChange={(e) => setPlantCode(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-bold font-mono text-foreground focus:ring-2 focus:ring-primary"
                  placeholder="e.g. 1001"
                />
              </div>
            </div>

            <div className="pt-2">
              <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                <Tag className="h-3.5 w-3.5 text-primary" />
                Expenditure Classification *
              </label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/20 cursor-pointer text-xs font-bold text-foreground hover:bg-muted/40 transition-colors">
                  <input
                    type="radio"
                    name="capexOpex"
                    value="OPEX"
                    checked={capexOpex === 'OPEX'}
                    onChange={() => setCapexOpex('OPEX')}
                    className="text-primary focus:ring-primary"
                  />
                  <span>Operating Expenditure (OPEX)</span>
                </label>

                <label className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/20 cursor-pointer text-xs font-bold text-foreground hover:bg-muted/40 transition-colors">
                  <input
                    type="radio"
                    name="capexOpex"
                    value="CAPEX"
                    checked={capexOpex === 'CAPEX'}
                    onChange={() => setCapexOpex('CAPEX')}
                    className="text-primary focus:ring-primary"
                  />
                  <span>Capital Expenditure (CAPEX)</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.NFA.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
